/**
 * Recursively flattens a nested attributes object into a flat
 * `Record<string, string>` suitable for Grafana Faro's log context.
 *
 * Faro's `LogContext` type is `Record<string, string>`. Passing nested objects
 * directly would cause Faro's internal `stringifyObjectValues()` to produce
 * `"[object Object]"` for every nested value, making them unqueryable in
 * Grafana. This function pre-flattens so that each leaf value arrives as a
 * labelled, queryable field.
 *
 * **Key construction**: ancestor keys are joined with `_`.
 * **Value coercion**:
 *   - `string`  → kept as-is
 *   - `Array`   → joined with `,` (elements coerced via `.toString()`)
 *   - `number` / `boolean` → `JSON.stringify`
 *   - plain `object` → recursed; contributes no key of its own
 *   - `null` / `undefined` → dropped
 *
 * @example
 * flattenAttributes({ foo: { bar: { baz: 5 } } })
 * // → { foo_bar_baz: '5' }
 *
 * @example
 * flattenAttributes({ service: { name: 'front-office', version: '1.2.3' }, retries: 3 })
 * // → { service_name: 'front-office', service_version: '1.2.3', retries: '3' }
 *
 * @example
 * flattenAttributes({ tags: ['a', 'b', 'c'] })
 * // → { tags: 'a,b,c' }
 *
 * @param attributes - The (possibly nested) log attributes to flatten.
 * @param prefix - Key prefix accumulated during recursion; callers omit this.
 * @returns A flat record whose values are all strings.
 */
export function flattenAttributes(
  attributes: Record<string, unknown>,
  prefix = '',
): Record<string, string> {
  const result: Record<string, string> = {}

  for (const [key, value] of Object.entries(attributes)) {
    if (value === undefined || value === null) continue

    const fullKey = prefix ? `${prefix}_${key}` : key

    if (typeof value === 'object' && !Array.isArray(value)) {
      Object.assign(
        result,
        flattenAttributes(value as Record<string, unknown>, fullKey),
      )
    } else if (Array.isArray(value)) {
      result[fullKey] = value.join(',')
    } else {
      result[fullKey] =
        typeof value === 'string' ? value : JSON.stringify(value)
    }
  }

  return result
}
